import { Box, Typography } from '@material-ui/core';
import React from 'react';
import { QueryObserverResult, RefetchOptions, useQuery } from 'react-query';

import { api } from 'api';
import { PaginatedResponse } from 'features/app/ts/PaginatedResponse';
import { Pagination } from 'features/app/components/Pagination';
import { SmallSpinner } from 'features/app/components/SmallSpinner';
import { useTranslation } from 'hooks/useTranslation';
import { useStyles } from './styles';

interface WithPaginationOptions {
  getEndpoint: string;
  queryId: string;
  pageSize: number;
  staleTime?: number;
  defaultFilters?: Record<string, unknown>;
}

export interface PaginatedWrappedComponentProps<T, F = Record<string, unknown>> {
  data?: T[];
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  isLoading: boolean;
  isFetching: boolean;
  error?: unknown;
  filters: F;
  setPage: (page: number) => void;
  setFilters: (filters: F) => void;
  toNextPage: () => void;
  toPreviousPage: () => void;
  refetch: (options?: RefetchOptions | undefined) => Promise<QueryObserverResult<T[], unknown>>;
}

export function withPagination<T, P>(
  { getEndpoint, queryId, pageSize, staleTime = 5, defaultFilters = {} }: WithPaginationOptions,
  Component: React.ComponentType<PaginatedWrappedComponentProps<T>>
) {
  return (props: P) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const [page, setPage] = React.useState(1);
    const [totalPages, setTotalPages] = React.useState(1);
    const [totalItems, setTotalItems] = React.useState(pageSize);
    const [filters, setFilters] = React.useState(defaultFilters);

    const fetchData = React.useCallback(async () => {
      const { data } = await api.get(getEndpoint, { params: { page, page_size: pageSize, ...filters } });
      const response = data as PaginatedResponse<T>;

      setTotalPages(response.pages_qty);
      setTotalItems(response.count);
      return response.results;
    }, [page, filters]);

    const { data, error, isLoading, isFetching, refetch } = useQuery([queryId, page, filters], fetchData, {
      keepPreviousData: true,
      staleTime,
    });

    const toNextPage = React.useCallback(() => {
      setPage(page + 1);
    }, [page]);

    const toPreviousPage = React.useCallback(() => {
      setPage(page - 1);
    }, [page]);

    const handlePageChange = React.useCallback((_: unknown, value: number) => {
      setPage(value);
    }, []);

    const wrappedSetFilters = React.useCallback((value: Record<string, unknown>) => {
      setPage(1);
      setFilters(value);
    }, []);

    return (
      <Box>
        <Component
          isFetching={isFetching}
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          totalItems={totalItems}
          data={data}
          error={error}
          filters={filters}
          setPage={setPage}
          setFilters={wrappedSetFilters}
          toNextPage={toNextPage}
          toPreviousPage={toPreviousPage}
          refetch={refetch}
          {...props}
        />
        <Box className={classes.paginationContainer}>
          <Typography variant="h6">
            {t('pagination.paginationSummary', { pageSize: pageSize > totalItems ? totalItems : pageSize, totalItems })}
            <SmallSpinner show={isFetching} pad="left" />
          </Typography>
          <Pagination
            count={totalPages}
            shape="rounded"
            page={page}
            onChange={handlePageChange}
            disabled={isLoading || isFetching}
          />
        </Box>
      </Box>
    );
  };
}
